import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Pages/Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  useEffect(() => {
    document.title = 'Contact Us - Pentagram Infotech';

    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Contact Pentagram Infotech for IT services, consulting, and support.'
      );
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      const res = await fetch('/api/send-mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const result = await res.json();

      if (!res.ok || !result.success) throw new Error();

      setSubmitStatus('success');
      setSubmitMessage('Message sent successfully. We will contact you shortly.');

      setFormData({
        name: '',
        email: '',
        company: '',
        service: '',
        message: ''
      });

    } catch {
      setSubmitStatus('error');
      setSubmitMessage(
        'Failed to send message. Please email info@pentagraminfotechtechnologies.com'
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const contactInfo = [
    {
      icon: 'bi-geo-alt',
      title: 'Headquarters – Bengaluru',
      details: [
        'Pentagram Infotech Pvt. Ltd.',
        'Esares Building, 22, Koramangala 80 Feet Road',
        'Bengaluru, Karnataka 560047, India'
      ]
    },
    {
      icon: 'bi-envelope',
      title: 'Contact Email',
      details: ['info@pentagraminfotechtechnologies.com']
    }
  ];

  const branches = [
    {
      id: 1,
      name: 'Bengaluru (HQ)',
      country: 'India',
      address:
        'Esares Building, 22, Koramangala 80 Feet Road, Bengaluru, Karnataka 560047',
      hours: 'Mon–Fri: 9:00 AM – 6:00 PM IST'
    },
    {
      id: 2,
      name: 'Hyderabad',
      country: 'India',
      address:
        'Plot No 1112, 2nd Floor, APHB Colony, Gachibowli, Hyderabad, Telangana 500032',
      hours: 'Mon–Fri: 9:00 AM – 6:00 PM IST'
    },
    {
      id: 3,
      name: 'Berlin',
      country: 'Germany',
      address: 'Friedrichstraße 123, 10117 Berlin',
      hours: 'Mon–Fri: 9:00 AM – 5:00 PM CET'
    },
    {
      id: 4,
      name: 'Singapore',
      country: 'Singapore',
      address: 'One Raffles Place, Singapore 048616',
      hours: 'Mon–Fri: 9:00 AM – 6:00 PM SGT'
    }
  ];

  const faqs = [
    {
      question: 'What industries do you serve?',
      answer: 'Healthcare, Finance, Education, Retail, Manufacturing, Government, and Technology.'
    },
    {
      question: 'Do you offer ongoing support?',
      answer: 'Yes. We provide maintenance, monitoring, updates, and 24/7 support packages.'
    },
    {
      question: 'What is your response time?',
      answer: 'We respond to all enquiries within 24 business hours.'
    },
    {
      question: 'Do you provide free consultation?',
      answer: 'Yes. We offer a free initial consultation.'
    }
  ];

  return (
    <div className="contact-page">

      {/* Hero */}
      <section className="contact-hero">
        <div className="container">
          <h1>Contact Us</h1>
          <p className="lead">
            Reach out to our team to discuss your business needs.
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="contact-info-section">
        <div className="container">
          <div className="row">
            {contactInfo.map((info, i) => (
              <div className="col-md-6 mb-4" key={i}>
                <div className="contact-card">
                  <i className={`bi ${info.icon}`}></i>
                  <h3>{info.title}</h3>
                  {info.details.map((d, j) => (
                    <p key={j}>{d}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Branches */}
      <section className="branches-section">
        <div className="container">
          <h2 className="section-title">Our Global Offices</h2>
          <div className="row">
            {branches.map(b => (
              <div className="col-md-6 col-lg-3 mb-4" key={b.id}>
                <div className="branch-card">
                  <h4>{b.name}</h4>
                  <span className="country-badge">{b.country}</span>
                  <p><i className="bi bi-geo-alt"></i> {b.address}</p>
                  <p><i className="bi bi-clock"></i> {b.hours}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="contact-form-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4">
              <h2>Send us a Message</h2>
              {submitStatus && (
                <div className={`alert alert-${submitStatus}`}>
                  {submitMessage}
                </div>
              )}
            </div>

            <div className="col-lg-6">
              <form onSubmit={handleSubmit} className="contact-form">
                <input type="text" name="name" placeholder="Your Name *" value={formData.name} onChange={handleChange} required />
                <input type="email" name="email" placeholder="Email Address *" value={formData.email} onChange={handleChange} required />
                <input type="text" name="company" placeholder="Company Name" value={formData.company} onChange={handleChange} />

                <select name="service" value={formData.service} onChange={handleChange} required>
                  <option value="" disabled>Select Service</option>
                  <option value="IT Services">IT Services</option>
                  <option value="Cloud Computing">Cloud Computing</option>
                  <option value="Cyber Security">Cyber Security</option>
                  <option value="Software Development">Software Development</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>

                <textarea name="message" placeholder="Your Message *" rows="5" value={formData.message} onChange={handleChange} required />

                <button type="submit" disabled={loading}>
                  {loading ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          {faqs.map((faq, i) => (
            <div key={i} className={`faq-item ${openFaqIndex === i ? 'active' : ''}`}>
              <div className="faq-question" onClick={() => toggleFaq(i)}>
                <h4>{faq.question}</h4>
                <span>{openFaqIndex === i ? '−' : '+'}</span>
              </div>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="contact-cta">
        <div className="container text-center">
          <h2>Ready to Transform Your Business?</h2>
          <Link to="/contact" className="btn btn-primary btn-lg">
            Start Your Project
          </Link>
        </div>
      </section>

    </div>
  );
};

export default Contact;
